import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ProfileView } from "@/components/dashboard/ProfileView";
import { LevelBadge } from "@/components/LevelBadge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Users, ClipboardList, Loader2, ArrowLeft, AlertTriangle, GraduationCap, Award, ListChecks, Lightbulb, HelpCircle } from "lucide-react";
import { skillMeta } from "@/lib/framework";

const ADVISOR_NAV_UNUSED = null;

export const ADVISOR_NAV = [
  { to: "/app/advisor", label: "My Students", icon: Users },
  { to: "/app/advisor/skills-update", label: "Skills Update", icon: ClipboardList },
  { to: "/app/advisor/academy", label: "Learning Academy", icon: GraduationCap },
  { to: "/app/advisor/certification", label: "TSB Certification", icon: Award },
  { to: "/app/tasks", label: "My Tasks", icon: ListChecks },
  { to: "/app/suggestions", label: "Submit a Suggestion", icon: Lightbulb },
  { to: "/app/help", label: "Help & Documentation", icon: HelpCircle },
];

export default function AdvisorHome() {
  const [students, setStudents] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/advisor/students").then(({ data }) => setStudents(data)); }, []);

  return (
    <DashboardShell title="Advisor & Faculty Portal" subtitle="Dr. Elena Vasquez · your assigned caseload" nav={ADVISOR_NAV}>
      {!students && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {students && (
        <Card className="p-2 card-elevate sm:p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Student ID</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>TSB</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Intervention flag</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((s, i) => (
                <TableRow key={s.id} data-testid={`advisor-student-row-${i}`} className="cursor-pointer"
                  onClick={() => navigate(`/app/advisor/${s.id}`)}>
                  <TableCell className="font-semibold text-[#003057]">{s.name}</TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-500" data-testid={`student-id-${i}`}>{s.student_number || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.program}</TableCell>
                  <TableCell className="font-mono-num font-bold">{s.tsb.toFixed(2)}</TableCell>
                  <TableCell><LevelBadge level={s.level} /></TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5" /> {s.weakest_skill.name} ({s.weakest_skill.score.toFixed(2)})
                    </span>
                  </TableCell>
                  <TableCell><Button variant="ghost" size="sm" data-testid={`advisor-view-${i}`}>View →</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </DashboardShell>
  );
}

export function AdvisorStudent() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/student/${id}`).then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [id]);

  return (
    <DashboardShell title={data ? data.name : "Student profile"} subtitle={data?.program?.name} nav={ADVISOR_NAV}>
      <Button variant="outline" className="mb-5 rounded-full" onClick={() => navigate("/app/advisor")} data-testid="advisor-back">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to caseload
      </Button>
      {err && <p className="text-red-600">{err}</p>}
      {!data && !err && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {data && (
        <>
          <Card className="mb-6 border-[#005291]/30 bg-[#005291]/5 p-5">
            <p className="flex items-center gap-2 font-heading text-base font-bold text-[#003057]">
              <ClipboardList className="h-5 w-5 text-[#005291]" /> Suggested intervention
            </p>
            {(() => {
              const weak = [...data.profile.skills].sort((a, b) => a.score - b.score)[0];
              return (
                <p className="mt-1.5 text-sm text-slate-700">
                  {data.name.split(" ")[0]}'s lowest skill is <strong>{weak.name}</strong> ({weak.score.toFixed(2)}, {weak.level_name}).
                  Recommend the <strong>“{skillMeta(weak.id)?.short} Accelerator”</strong> Learning Academy module and a check-in next term.
                </p>
              );
            })()}
          </Card>
          <ProfileView data={data} careerLimit={6} />
        </>
      )}
    </DashboardShell>
  );
}
